/**
 * Reusable single-section shell for an integration data tab. One section = one
 * `integration_runs` key synced by one "Sync now" button, surfacing N related
 * entity tables as inner sub-tabs (e.g. Solutions = Articles | Categories |
 * Folders). The pending Contacts plan reuses this with Companies | Contacts;
 * non-Freshdesk integrations (skimmer / register) reuse it the same way.
 *
 * The shell owns the sync trigger (+ 409 handling), its own run-history fetch and
 * collapse, and the URL-driven sub-tab (`view`). Each sub-tab's filtering / sort /
 * pagination is delegated to `IntegrationEntityTable` (the standard admin-list
 * trio), so the consumer only supplies the data + entity configs. The header is
 * card-less - heading + icon on the left, last-sync + Sync now on the right - with
 * the recent-runs table shown in its own card only when expanded. Per-entity totals
 * live on the sub-tab labels, so the header carries no separate counts.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { useDataFetching } from '../../../../../hooks/useDataFetching'
import { useNotify } from '../../../../../components/shared/ToastProvider'
import {
  integrationsApi,
  IntegrationRun,
} from '../../../../../services/admin/integrationsApi'
import { poolwerxColors } from '../../../../../theme/colors'
import IntegrationRunsTable from './IntegrationRunsTable'
import IntegrationEntityTable from './IntegrationEntityTable'
import { relativeTime } from './relativeTime'
import { deriveRunDisplay } from './runStatus'
import { anyRunActive, usePollWhileRunning } from './usePollWhileRunning'
import type { SectionCustomTab, SectionTab } from './integrationSection.types'

export type {
  SectionColumn,
  SectionEntityTable,
  SectionExtraFilter,
  SectionCustomTab,
  SectionTab,
} from './integrationSection.types'

/** Custom (non-table) sub-tabs carry a `render`; entity tables do not. */
const isCustomTab = (t: SectionTab): t is SectionCustomTab => 'render' in t

interface IntegrationSectionTabProps {
  /** Section heading (e.g. "Solutions knowledge base"). */
  title: string
  /** Icon shown to the left of the heading. */
  titleIcon?: React.ReactNode
  /** The integration run-key triggered by Sync now (e.g. "freshdesk_solutions"). */
  runKey: string
  /** Short noun for toasts (e.g. "Solutions" -> "Solutions sync started"). */
  syncLabel: string
  /** True while the section data is loading (shows a spinner). */
  loading?: boolean
  entities: SectionTab[]
  /** Called after a sync is kicked off, to refetch the section data. */
  onSynced?: () => void
}

// Two-line "last sync" summary sitting beside Sync now: a status line
// (icon + "Last sync successful/failed") over the relative sync time. When
// `onToggle` is given (i.e. there are runs to show) the whole block doubles as
// the subtle toggle for the run history - a hover highlight + a rotating
// chevron - so we don't need a separate "View recent runs" link.
const LastSyncSummary: React.FC<{
  run?: IntegrationRun
  expanded?: boolean
  onToggle?: () => void
}> = ({ run, expanded, onToggle }) => {
  const display = run ? deriveRunDisplay(run) : null
  if (!run || !display) {
    return (
      <Typography variant="body2" color="text.secondary">
        Never synced
      </Typography>
    )
  }
  const isError = display.color === 'error'
  const isSuccess = display.color === 'success'
  // While running, show a small spinner + "Sync in progress" + "Started x ago"
  // rather than the (now-misleading) "Synced x ago" success wording.
  const Icon = isSuccess ? CheckCircleIcon : isError ? ErrorIcon : null
  const color = isSuccess ? 'success.main' : isError ? 'error.main' : 'text.disabled'
  const statusLine = display.isRunning
    ? 'Sync in progress'
    : isSuccess
      ? 'Last sync successful'
      : isError
        ? 'Last sync failed'
        : display.label
  const when = display.isRunning
    ? relativeTime(run.started_at, '')
    : relativeTime(run.finished_at ?? run.started_at, '')
  const whenPrefix = display.isRunning ? 'Started' : 'Synced'

  const body = (
    <>
      {display.isRunning ? (
        <CircularProgress size={16} sx={{ color: poolwerxColors.teal }} />
      ) : (
        Icon && <Icon sx={{ fontSize: 20, color }} />
      )}
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
          {statusLine}
        </Typography>
        {when && (
          <Typography variant="caption" color="text.secondary">
            {whenPrefix} {when}
          </Typography>
        )}
      </Box>
      {onToggle && (
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transition: 'transform .2s',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        />
      )}
    </>
  )

  if (!onToggle) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {body}
      </Stack>
    )
  }

  return (
    <ButtonBase
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={expanded ? 'Hide recent sync runs' : 'Show recent sync runs'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 1,
        px: 1,
        py: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {body}
    </ButtonBase>
  )
}

const IntegrationSectionTab: React.FC<IntegrationSectionTabProps> = ({
  title,
  titleIcon,
  runKey,
  syncLabel,
  loading,
  entities,
  onSynced,
}) => {
  const notify = useNotify()
  const [searchParams, setSearchParams] = useSearchParams()
  const [triggering, setTriggering] = useState(false)
  const [showRuns, setShowRuns] = useState(false)

  const { data: runs, refetch: refetchRuns } = useDataFetching<IntegrationRun[]>({
    fetchFn: () => integrationsApi.listIntegrationRuns(runKey, 10),
    dependencies: [runKey],
  })
  const lastRun = runs?.[0]

  // Poll while a run is in flight, and surface the runs panel so the user sees
  // it progress - covers a scheduled run or a reload mid-run, not just the local
  // Sync now click.
  usePollWhileRunning(runs, refetchRuns)
  const anyRunning = anyRunActive(runs)
  useEffect(() => {
    if (anyRunning) setShowRuns(true)
  }, [anyRunning])

  // Active sub-tab is URL-driven (`view`) so it is bookmarkable and the
  // cross-tab count links land on the right table.
  const active =
    entities.find((e) => e.key === searchParams.get('view')) ?? entities[0]

  const handleTabChange = (key: string) => {
    // A manual tab switch starts clean - strip every entity's URL-bound filter
    // params (each extraFilter owns its own param names via clear()), so the
    // shell needn't know them, then switch the active sub-tab.
    const next = entities.reduce(
      (acc, e) => (!isCustomTab(e) && e.extraFilter ? e.extraFilter.clear(acc) : acc),
      new URLSearchParams(searchParams),
    )
    next.set('view', key)
    setSearchParams(next)
  }

  const syncNow = async () => {
    setTriggering(true)
    setShowRuns(true) // surface the run history so the user sees it land
    try {
      await integrationsApi.triggerIntegrationRun(runKey)
      notify.showSuccess(`${syncLabel} sync started`)
      // Completes in the background; refetch shortly after.
      setTimeout(() => {
        onSynced?.()
        refetchRuns()
      }, 2000)
    } catch (err: any) {
      if (err?.response?.status === 409) {
        notify.showInfo(`A ${syncLabel} sync is already running`)
      } else {
        notify.showError(`Failed to start ${syncLabel} sync`)
      }
    } finally {
      setTriggering(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            {titleIcon && (
              <Box
                sx={{
                  color: poolwerxColors.teal,
                  display: 'flex',
                  '& > svg': { fontSize: 28 },
                }}
              >
                {titleIcon}
              </Box>
            )}
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: poolwerxColors.teal }}
            >
              {title}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <LastSyncSummary
              run={lastRun}
              expanded={showRuns}
              onToggle={
                runs && runs.length > 0 ? () => setShowRuns((v) => !v) : undefined
              }
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={
                triggering || anyRunning ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={syncNow}
              disabled={triggering || anyRunning}
            >
              {anyRunning ? 'Syncing…' : 'Sync now'}
            </Button>
          </Stack>
        </Stack>

        <Collapse in={showRuns}>
          <Paper variant="outlined" sx={{ p: 2, mt: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2">Recent sync runs</Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={() => setShowRuns(false)}
                aria-label="Hide recent runs"
              >
                <ExpandLessIcon fontSize="small" />
              </IconButton>
            </Stack>
            <IntegrationRunsTable runs={runs ?? []} maxRows={5} />
          </Paper>
        </Collapse>
      </Box>

      <Box>
        <Tabs
          value={active?.key}
          onChange={(_, v) => handleTabChange(v)}
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTabs-indicator': { backgroundColor: poolwerxColors.teal },
            '& .Mui-selected': { color: `${poolwerxColors.teal} !important` },
          }}
        >
          {entities.map((e) => (
            <Tab
              key={e.key}
              value={e.key}
              icon={(e.icon as React.ReactElement) || undefined}
              iconPosition="start"
              label={isCustomTab(e) ? e.label : `${e.label} (${e.rows.length})`}
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>

        {active &&
          (isCustomTab(active) ? (
            <Box key={active.key}>{active.render()}</Box>
          ) : (
            <IntegrationEntityTable
              key={active.key}
              entity={active}
              params={searchParams}
              setParams={setSearchParams}
            />
          ))}
      </Box>
    </Stack>
  )
}

export default IntegrationSectionTab
